import { usePage } from '@inertiajs/react'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { messages } from './messages'

/**
 * Marketing-only i18n (trilingual). Scoped to the public surface so it never
 * imports operator/portal context. Locales:
 *   - en       English (default)
 *   - zh-Hant  繁體中文 (Traditional — Taiwan / HK)
 *   - zh-Hans  简体中文 (Simplified — Mainland China)
 *
 * zh-Hant and zh-Hans are distinct translations — never auto-converted from one
 * to the other (per docs/marketing/tasks/ROADMAP.md). Fallback chain is
 * locale → en → key.
 */
export const LOCALES = ['en', 'zh-Hant', 'zh-Hans'] as const
export type Locale = (typeof LOCALES)[number]

const STORAGE_KEY = 'g4-lang'
const DEFAULT_LOCALE: Locale = 'en'

export const LOCALE_LABELS: Record<Locale, { short: string; full: string }> = {
  en: { short: 'EN', full: 'English' },
  'zh-Hant': { short: '繁中', full: '繁體中文' },
  'zh-Hans': { short: '简中', full: '简体中文' },
}

function isLocale(value: string | null): value is Locale {
  return value !== null && (LOCALES as readonly string[]).includes(value)
}

function readStoredLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE
  const stored = window.localStorage.getItem(STORAGE_KEY)
  return isLocale(stored) ? stored : DEFAULT_LOCALE
}

type I18nContextValue = {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string) => string
  /** True when rendered inside the operator CMS live preview (?preview=1). */
  preview: boolean
}

const I18nContext = createContext<I18nContextValue | null>(null)

// Operator-editable copy (CMS, iteration 0009) arrives as an i18n override map on
// the Inertia page props and is merged *over* the baked-in `messages`, per locale.
// Absent/blank keys fall back to the bundle, so the site works with no CMS data.
type Overrides = Partial<Record<Locale, Record<string, string>>>

// Stable empty fallback so an override-less render keeps the same identity and the
// merge memos below don't recompute every render.
const NO_OVERRIDES: Overrides = {}

// CMS live-preview postMessage protocol (iteration 0009). The operator editor
// (a different origin) pushes the current draft here; validated against the
// server-provided allowlist. The strings are duplicated in the operator bundle —
// they can't be shared across surfaces per the isolation rule.
const PREVIEW_SOURCE = 'g4-cms'
type PreviewProp = { active?: boolean; allowed_origins?: string[] }

// Scroll the preview to (and briefly highlight) the first element rendering the
// given text — used for focus-follow from the operator editor. Text-based so it
// needs no per-element tagging on the marketing pages.
function scrollPreviewToText(text: string): void {
  const needle = text.trim()
  if (!needle || typeof document === 'undefined') return
  // Find the tightest element whose text contains the value (textContent, not a
  // single text node — the copy is often split by inline <em>/<span>). The
  // smallest matching element is the most specific wrapper.
  let best: HTMLElement | null = null
  for (const el of document.body.querySelectorAll<HTMLElement>('*')) {
    if (el.getAttribute('role') === 'status') continue // skip the preview banner
    const tc = el.textContent
    if (tc?.includes(needle) && (!best || tc.length < (best.textContent?.length ?? Infinity))) {
      best = el
    }
  }
  if (!best) return
  best.scrollIntoView({ behavior: 'smooth', block: 'center' })
  const previous = best.style.boxShadow
  best.style.transition = 'box-shadow 0.2s ease'
  best.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.85)'
  window.setTimeout(() => {
    if (best) best.style.boxShadow = previous
  }, 1300)
}

function readProp<T>(key: string): T | undefined {
  try {
    // biome-ignore lint/correctness/useHookAtTopLevel: invoked once per render; only the throwing test-mock access is guarded
    return usePage().props[key] as T | undefined
  } catch {
    // Some unit tests mount without an Inertia page context.
    return undefined
  }
}

// Live draft overrides received from the operator editor over postMessage, plus
// an editor-driven locale. Only wired up when preview is active. Returns the draft
// layer to merge on top of the saved overrides.
function usePreviewDraft(
  active: boolean,
  allowedOrigins: string[],
  setLocale: (locale: Locale) => void,
): Overrides {
  const [draft, setDraft] = useState<Overrides>(NO_OVERRIDES)

  useEffect(() => {
    if (!active || typeof window === 'undefined') return

    const allowed = new Set(allowedOrigins)
    function onMessage(event: MessageEvent) {
      if (!allowed.has(event.origin)) return
      const data = event.data as
        | { source?: string; type?: string; overrides?: Overrides; locale?: string; text?: string }
        | undefined
      if (!data || data.source !== PREVIEW_SOURCE) return
      if (data.type === 'preview:update') {
        if (data.overrides) setDraft(data.overrides)
        if (data.locale && isLocale(data.locale)) setLocale(data.locale)
      } else if (data.type === 'preview:scrollto' && typeof data.text === 'string') {
        scrollPreviewToText(data.text)
      }
    }
    window.addEventListener('message', onMessage)

    // Announce readiness so the editor pushes the current draft immediately —
    // to both the opener (new-tab preview) and the parent (docked iframe).
    const ready = { source: PREVIEW_SOURCE, type: 'preview:ready' }
    for (const origin of allowed) {
      window.opener?.postMessage(ready, origin)
      if (window.parent !== window) window.parent.postMessage(ready, origin)
    }

    return () => window.removeEventListener('message', onMessage)
  }, [active, allowedOrigins, setLocale])

  return draft
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale)

  const saved = readProp<Overrides>('marketing_content') ?? NO_OVERRIDES
  const preview = readProp<PreviewProp>('preview') ?? {}
  const previewActive = preview.active === true
  const allowedOrigins = useMemo(() => preview.allowed_origins ?? [], [preview.allowed_origins])

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next)
    if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, next)
  }, [])

  // In preview the editor drives the locale (don't persist that to the visitor).
  const draft = usePreviewDraft(previewActive, allowedOrigins, setLocaleState)

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  // Merge order: bundled copy < saved overrides < live draft.
  const merged = useMemo(() => {
    const result = {} as Record<Locale, Record<string, string>>
    for (const loc of LOCALES) {
      result[loc] = { ...messages[loc], ...(saved[loc] ?? {}), ...(draft[loc] ?? {}) }
    }
    return result
  }, [saved, draft])

  const t = useCallback(
    (key: string) => merged[locale][key] ?? merged.en[key] ?? key,
    [locale, merged],
  )

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t, preview: previewActive }),
    [locale, setLocale, t, previewActive],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used within an I18nProvider')
  return ctx
}
